<template>
  <div class="qf-table-row" v-bind="$attrs" :class="{ 'active-row': activeRowKeys.includes(row?.[rowKey]), 'expand-row': expandedRowKeys.includes(row?.[rowKey]), 'error-row': errorRowList?.includes(row?.[rowKey]), 'qf-table-first-row': rowIndex == 0, 'qf-table-last-row': isLastRow, 'row-checkboxed': checkedParams.checked?.includes(row?.id) }" :style="{ zIndex: rowZIdex }" @click="handleRowClick($event)" @mousedown="dragstart">
    <div class="qf-table-fixed-left" ref="qfTableFixedLeftRef" v-if="hasLeftColumns" :class="{ 'active-row': activeRowKeys == row?.[rowKey], 'expand-row': expandedRowKeys.includes(row?.[rowKey]), 'error-row': errorRowList?.includes(row?.[rowKey]), 'scroll-left-shadow': scrollShadowLeft, 'row-checkboxed': checkedParams.checked?.includes(row?.id) }">
      <div class="corner-mark" v-if="cornerMark">
        <slot name="cornerMark" :row="row"></slot>
      </div>
      <div v-if="rowDragParams.enabled" ref="dragBoxRef" class="qf-table-drag-box" :class="{ disabled: dragDisabled }">
        <jw-tooltip :content="dragDisabledTip" :disabled="!dragDisabledTip">
          <slot name="dragIcon" :rowKey="row?.[rowKey]">
            <qfei-icon-more-two-outlined size="16" />
          </slot>
        </jw-tooltip>
      </div>
      <div class="qf-table-expand-box" v-if="expandIcon" @click="handleExpand($event)">
        <slot name="expandIcon" :rowKey="row?.[rowKey]">
          <icon-right-circle size="16" />
        </slot>
      </div>
      <div class="qf-table-row-checkbox" :class="checkBoxClassName" :style="{ width: checkedParams.width + 'px' }" v-if="checkedParams.enabled">
        <slot name="customCheckbox" :row="row" :index="rowIndex" :change="handleChange">
          <jw-checkbox :model-value="idLists?.includes(row?.[rowKey])" @change="handleChange($event, row?.[rowKey])"></jw-checkbox>
        </slot>
      </div>
      <qf-table-cell v-for="(column, colIndex) in colLeft" :key="column.colKey" :column="column" :row="row" :colIndex="colIndex">
        <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
          <slot :name="name" v-bind="slotProps"></slot>
        </template>
        <slot :name="`table-cell-suffix-${colIndex}-${colIndex}`" :row="row" :width="column.width"></slot>
      </qf-table-cell>
    </div>
    <div class="qf-table-content" :class="{ 'row-checkboxed': checkedParams.checked?.includes(row?.id) }" v-if="colMiddle?.length" ref="qfTableContentRef">
      <template v-for="(column, colIndex) in colMiddle" :key="column.colKey">
        <div v-if="column.children?.length" class="sub-form-body">
          <qf-table-cell v-for="(group, groupIndex) in column.children" :key="group.colKey" :column="group" :row="row" :colIndex="groupIndex">
            <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
              <slot :name="name" v-bind="slotProps"></slot>
            </template>
            <slot :name="`table-cell-suffix-${colLeft?.length + colIndex}-${groupIndex}`" :row="row" :width="group.width"></slot>
          </qf-table-cell>
        </div>
        <qf-table-cell v-else :column="column" :row="row" :colIndex="colIndex">
          <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
            <slot :name="name" v-bind="slotProps"></slot>
          </template>
          <slot :name="`table-cell-suffix-${colLeft?.length + colIndex}-${colLeft?.length + colIndex}`" :row="row" :width="column.width"></slot>
        </qf-table-cell>
      </template>
    </div>
    <div class="qf-table-fixed-right" v-if="colRight?.length" ref="qfTableFixedRightRef" :class="{ 'active-row': activeRowKeys == row?.[rowKey], 'expand-row': expandedRowKeys.includes(row?.[rowKey]), 'error-row': errorRowList?.includes(row?.[rowKey]), 'scroll-right-shadow': scrollShadowRight, 'row-checkboxed': checkedParams.checked?.includes(row?.id) }">
      <qf-table-cell v-for="(column, colIndex) in colRight" :key="column.colKey" :column="column" :row="row" :colIndex="colIndex">
        <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
          <slot :name="name" v-bind="slotProps"></slot>
        </template>
      </qf-table-cell>
    </div>
  </div>
  <div v-if="expandIcon && expandedRowKeys?.includes(row?.[rowKey])" class="qf-table-expand-row-full">
    <div class="qf-table-expand-row-full-container" :style="{ width: `${expandRowWidth}px` }">
      <slot name="expandRow" v-bind="{ row: row, rowIndex }"> </slot>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onUnmounted, ref } from 'vue';
import QfTableCell from '../qf-table-cell/qf-table-cell.vue';
import { tools } from '@/utils/tools';
import { QfeiIconMoreTwoOutlined } from '@qfei-design/icons-vue';

const props = defineProps({
  row: {
    type: Object,
    default: () => {}
  },
  rowIndex: {
    type: Number,
    default: 0
  },
  columns: {
    type: Array,
    default: () => []
  },
  rowKey: {
    type: String,
    default: 'id'
  },
  hoveredRow: {
    type: Number,
    default: 0
  },
  expandIcon: {
    type: [Boolean, String],
    default: false
  },
  expandedRowKeys: {
    type: Array,
    default: () => []
  },
  checkedParams: {
    type: Object,
    default: () => ({
      enabled: true,
      type: 'checkbox',
      width: 40,
      checked: []
    })
  },
  activeRowKeys: {
    type: Array,
    default: () => []
  },
  errorRowList: {
    type: Array,
    default: () => []
  },
  checkBoxClassName: {
    type: String,
    default: ''
  },
  cornerMark: {
    type: Boolean,
    default: false
  },
  isLastRow: {
    type: Boolean,
    default: false
  }
});
const randomKey = ref('');
const idLists = ref(inject('qfTable')?.idList);
const expandRowWidth = ref(inject('qfTable')?.qfTableWidth);
const scrollShadowLeft = ref(inject('qfTable')?.scrollShadowLeft);
const scrollShadowRight = ref(inject('qfTable')?.scrollShadowRight);
const emits = defineEmits(['rowClick', 'handleChecked', 'update:expandedRowKeys', 'onDragstart']);
const qfTableFixedLeftRef = ref(null);
const qfTableContentRef = ref(null);
const qfTableFixedRightRef = ref(null);
const rowDragParams = inject('qfTable')?.rowDragParams;
const dragBoxRef = ref(null);
const dragDisabled = computed(() => {
  const { disabled } = rowDragParams.value;
  if (typeof disabled === 'function') {
    return disabled(props.row);
  }
  return disabled;
});
const dragDisabledTip = computed(() => {
  const { disabledTip } = rowDragParams.value;
  if (!dragDisabled.value || !disabledTip) return '';
  if (typeof disabledTip === 'function') {
    return disabledTip(props.row);
  }
  return disabledTip;
});

// 优化展开行逻辑
const handleExpand = (e) => {
  emits('update:expandedRowKeys', e, props.row);
};
// 刷新行数据
const refreshRow = (rk) => {
  if (rk == props.row?.[props.rowKey]) {
    randomKey.value = tools.randomString(6);
  }
};
// 优化行点击逻辑
const handleRowClick = (e) => {
  emits('rowClick', { e, row: props.row });
};

// 优化单选逻辑
const handleChange = (value, id) => {
  emits('handleChecked', value, id);
};
//鼠标悬浮行
// const handleMouseover = () => {
//   emits('rowMouseover', { rowKey: props.row[props.rowKey] });
// };
// 鼠标移出行
// const handleMouseLeave = () => {
//   emits('rowMouseLeave', { rowKey: props.row[props.rowKey] });
// };
// 计算列的固定位置
const colLeft = computed(() => props.columns?.filter((v) => v.fixed === 'left'));
const colMiddle = computed(() => props.columns?.filter((v) => v.fixed !== 'left' && v.fixed !== 'right'));
const colRight = computed(() => props.columns?.filter((v) => v.fixed === 'right'));

// 是否有左固定列
const hasLeftColumns = computed(() => colLeft.value?.length || props.checkedParams.enabled || props.expandIcon || props.cornerMark || rowDragParams.value.enabled);
const rowZIdex = ref(null);
// 提供修改行数据的z-index方法
const updateZIndex = (zIndex) => {
  rowZIdex.value = zIndex;
};
/**
 * @description 拖动行事件开始
 * @param {MouseEvent} e
 */
const dragstart = (e) => {
  // 确保允许拖动且点击了拖动图标
  if (dragDisabled.value || !dragBoxRef.value || !dragBoxRef.value.contains(e.target)) return;
  emits('onDragstart', e);
  e.preventDefault();
};
onUnmounted(() => {
  qfTableFixedLeftRef.value = null;
  qfTableContentRef.value = null;
  qfTableFixedRightRef.value = null;
  randomKey.value = null;
});
defineExpose({ refreshRow, updateZIndex });
</script>

<style scoped lang="less">
@import './qf-table-row.less';
</style>
